import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Persistent club switcher rendered in the top nav for any member who belongs
 * to two or more *active* clubs. Picking a club POSTs to
 * /auth/me/active-club, which updates `users.club_id` server-side; the
 * AuthContext `user` is then refreshed so every downstream query (orders,
 * wishlist, dashboard stats) automatically re-routes to the new tenant.
 *
 * Hidden entirely for:
 *  - staff (single-tenant by construction)
 *  - members with only one active membership
 *  - users whose memberships array doesn't carry club_id (legacy single-tenant)
 */
export default function ClubSwitcher() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handlePick = useCallback(async (clubId) => {
    if (!clubId || clubId === user?.club_id || busy) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/me/active-club", { club_id: clubId });
      setUser(data);
      setOpen(false);
      // Full reload so tenant-scoped pages (dashboard / orders / wishlist)
      // refetch from the new active club. Cheaper than wiring an event bus.
      window.location.reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not switch clubs.");
    } finally {
      setBusy(false);
    }
  }, [busy, setUser, user?.club_id]);

  if (!user || user.role !== "member") return null;
  const memberships = (user.clubs || []).filter(
    (c) => c.club_id && (c.status || "active") === "active",
  );
  if (memberships.length < 2) return null;

  const activeId = user.club_id;
  const activeMembership = memberships.find((c) => c.club_id === activeId) || memberships[0];

  return (
    <div ref={wrapRef} className="relative" data-testid="club-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        data-testid="club-switcher-trigger"
        className="flex items-center gap-2 px-3 py-1.5 border border-[#E5E3D8] bg-[#F9F8F4] hover:bg-[#F3F1E9] transition-colors disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="hidden sm:block text-left leading-tight">
          <span className="block overline">Active Club</span>
          <span
            className="block font-serif text-sm text-[#1A422D] truncate max-w-[140px]"
            data-testid="club-switcher-active-name"
          >
            {activeMembership?.name || "Choose a club"}
          </span>
        </span>
        <span
          className="sm:hidden font-serif text-sm text-[#1A422D] truncate max-w-[140px]"
          data-testid="club-switcher-active-name-mobile"
        >
          {activeMembership?.name || "Choose a club"}
        </span>
        <ChevronDown
          size={16}
          className={`text-[#7A7A7A] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="club-switcher-menu"
          className="absolute right-0 sm:left-0 mt-2 w-72 bg-[#FFFFFF] border border-[#E5E3D8] shadow-xl z-50"
        >
          <div className="px-4 py-3 border-b border-[#E5E3D8]">
            <div className="overline">Switch Club</div>
            <div className="text-xs text-[#7A7A7A] mt-1">
              You belong to {memberships.length} active clubs.
            </div>
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {memberships.map((c) => {
              const isActive = c.club_id === activeId;
              return (
                <li key={c.club_id}>
                  <button
                    type="button"
                    onClick={() => handlePick(c.club_id)}
                    disabled={busy}
                    data-testid={`club-switcher-option-${c.slug || c.club_id}`}
                    className={`w-full flex items-start justify-between gap-3 px-4 py-3 text-left transition-colors disabled:opacity-60 ${
                      isActive ? "bg-[#E9F0EB]" : "hover:bg-[#F3F1E9]"
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-serif text-base text-[#1A1A1A] truncate">
                        {c.name}
                      </div>
                      <div className="text-xs text-[#7A7A7A] mt-0.5 flex items-center gap-2">
                        {c.member_number && (
                          <span className="font-mono">#{c.member_number}</span>
                        )}
                        {c.is_home && (
                          <span className="px-1.5 py-0.5 border border-[#B59A5A] text-[10px] tracking-[0.16em] text-[#8a6f33]">
                            HOME
                          </span>
                        )}
                      </div>
                    </div>
                    {isActive && (
                      <Check size={16} className="text-[#1A422D] mt-1 shrink-0" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-3 border-t border-[#E5E3D8] flex items-center justify-between gap-3">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              data-testid="club-switcher-manage"
              className="text-xs uppercase tracking-[0.18em] text-[#1A422D] hover:underline"
            >
              Manage memberships →
            </Link>
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              data-testid="club-switcher-join-another"
              className="flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[#8a6f33] hover:underline"
            >
              <Plus size={12} /> Join another
            </Link>
          </div>
          {error && (
            <div
              data-testid="club-switcher-error"
              className="px-4 py-2 text-xs text-[#8a1a1a] border-t border-[#E5E3D8] bg-[#FFF5F5]"
            >
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
